import {
  FULL_PERCENT,
  MAX_SPLIT_VALUE,
  SPLIT_MODES,
  SPLIT_SCALE,
  splitByWeights,
  splitSharesById,
} from "./split";

export {
  FULL_PERCENT,
  MAX_SPLIT_VALUE,
  splitByPercent,
  splitByWeights,
} from "./split";

// Keep in sync with server/validation.js.
export const MAX_CENTS = 99_999_999;

// The API may send numeric strings (SQL SUM).
export const toCents = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

// Every non-digit is dropped: "12,34 €" -> 1234.
export const parseCents = (value) => {
  const digits = `${value ?? ""}`.replace(/\D/g, "");
  return digits ? Math.min(parseInt(digits, 10), MAX_CENTS) : 0;
};

export const splitAmount = (totalCents, receivers) => {
  if (receivers.length === 0) return [];
  if (totalCents < 0) {
    return splitAmount(-totalCents, receivers).map((r) => ({
      ...r,
      share: 0 - r.share,
    }));
  }

  const base = Math.floor(totalCents / receivers.length);
  let remainder = totalCents % receivers.length;

  return receivers.map((r) => ({
    ...r,
    share: base + (remainder-- > 0 ? 1 : 0),
  }));
};

export const rebalance = (totalCents, receivers) => {
  const unlocked = receivers.filter((r) => !r.locked);
  const remaining = totalCents - sumShares(receivers.filter((r) => r.locked));
  if (remaining < 0 || unlocked.length === 0) return receivers;

  const spread = splitAmount(remaining, unlocked);
  let next = 0;
  return receivers.map((r) => (r.locked ? r : spread[next++]));
};

export const sumShares = (receivers) =>
  receivers.reduce((sum, r) => sum + r.share, 0);

export const getRemainingCents = (totalCents, receivers) =>
  totalCents - sumShares(receivers);

const unlockAll = (receivers) =>
  receivers.map((r) => ({ ...r, locked: false }));

// Invariant of the expense popup: the shares always add up to the total.
export const rebalanceOrSplit = (totalCents, receivers) => {
  const result = rebalance(totalCents, receivers);
  return sumShares(result) === totalCents
    ? result
    : splitAmount(totalCents, unlockAll(receivers));
};

const MAX_WEIGHT_SUM = 90_000_000; // sum² < Number.MAX_SAFE_INTEGER

// Largest remainder method.
export const scaleShares = (totalCents, weights) => {
  if (weights.length === 0) return [];
  let sum = weights.reduce((acc, w) => acc + w, 0);
  if (sum <= 0) {
    return splitAmount(
      totalCents,
      weights.map(() => ({})),
    ).map((r) => r.share);
  }

  // total * w / sum = q * w + r * w / sum; r * w < sum² must stay a safe
  // integer, so larger weights are scaled down (ratios change by < 1e-7).
  if (sum > MAX_WEIGHT_SUM) {
    weights = weights.map((w) => Math.floor((w / sum) * MAX_WEIGHT_SUM));
    sum = weights.reduce((acc, w) => acc + w, 0);
  }
  const q = Math.floor(totalCents / sum);
  const r = totalCents % sum;
  const parts = weights.map((w, index) => ({
    index,
    share: q * w + Math.floor((r * w) / sum),
    remainder: (r * w) % sum,
  }));

  let left = totalCents - parts.reduce((acc, p) => acc + p.share, 0);
  [...parts]
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
    .forEach((p) => {
      if (left > 0) {
        p.share += 1;
        left -= 1;
      }
    });
  return parts.map((p) => p.share);
};

const weightOf = (r) => r.weight ?? r.share;

// `weight` is the split the user last defined; amount changes only rescale
// `share` from it, so typing through small amounts or 0 never loses the split.
// Call after every change that is not an amount change.
export const captureWeights = (receivers) => {
  const unlockedShares = receivers.filter((r) => !r.locked).map((r) => r.share);
  const isEqual =
    unlockedShares.length > 0 &&
    Math.max(...unlockedShares) - Math.min(...unlockedShares) <= 1;
  const equalWeight = isEqual ? Math.max(...unlockedShares) : 0;
  return receivers.map((r) => ({
    ...r,
    weight: !r.locked && isEqual ? equalWeight : r.share,
  }));
};

const releaseUnhonouredLocks = (receivers) =>
  receivers.map((r) =>
    r.locked && r.weight !== undefined && r.share !== r.weight
      ? { ...r, locked: false }
      : r,
  );

export const resplitForAmount = (totalCents, receivers) => {
  const lockedSum = receivers
    .filter((r) => r.locked)
    .reduce((sum, r) => sum + weightOf(r), 0);
  const unlocked = receivers.filter((r) => !r.locked);
  const locksFit =
    lockedSum <= totalCents &&
    (unlocked.length > 0 || lockedSum === totalCents);

  if (!locksFit) {
    const scaled = scaleShares(totalCents, receivers.map(weightOf));
    return receivers.map((r, i) => ({ ...r, share: scaled[i] }));
  }

  const scaled = scaleShares(totalCents - lockedSum, unlocked.map(weightOf));
  let next = 0;
  return receivers.map((r) => ({
    ...r,
    share: r.locked ? weightOf(r) : scaled[next++],
  }));
};

export const toggleReceiverShare = (totalCents, receivers, id) => {
  const current = releaseUnhonouredLocks(receivers);
  const updated = current.some((r) => r.id === id)
    ? current.filter((r) => r.id !== id)
    : [...current, { id, share: 0, locked: false }];
  return captureWeights(rebalanceOrSplit(totalCents, updated));
};

export const equalSplit = (totalCents, receivers) =>
  captureWeights(
    splitAmount(
      totalCents,
      receivers.map((r) => ({ id: r.id, locked: false })),
    ),
  );

export const setManualShare = (totalCents, receivers, id, cents) => {
  if (!receivers.some((r) => r.id === id)) return receivers;

  const share = Math.min(Math.max(cents, 0), totalCents);
  let updated = releaseUnhonouredLocks(receivers).map((r) =>
    r.id === id ? { ...r, share, locked: true } : r,
  );

  const others = updated.filter((r) => r.id !== id);
  const lockedSum = share + sumShares(others.filter((r) => r.locked));
  const someoneTakesRest = others.some((r) => !r.locked);
  if (
    lockedSum > totalCents ||
    (!someoneTakesRest && lockedSum !== totalCents)
  ) {
    updated = updated.map((r) => (r.id === id ? r : { ...r, locked: false }));
  }

  return captureWeights(rebalanceOrSplit(totalCents, updated));
};

export const SHARE_MODES = {
  equal: "equal",
  amounts: "amounts",
  weights: SPLIT_MODES.weights,
  percent: SPLIT_MODES.percent,
};

export const isValueMode = (mode) =>
  mode === SHARE_MODES.weights || mode === SHARE_MODES.percent;

const DEFAULT_WEIGHT = SPLIT_SCALE;

const SPLIT_VALUE_PATTERN = /^(\d*)(?:[.,](\d*))?$/;

export const parseSplitValue = (text) => {
  const match = SPLIT_VALUE_PATTERN.exec(`${text ?? ""}`.trim());
  if (!match || (match[1] === "" && !match[2])) return null;
  const whole = Number(match[1] || 0);
  const fraction = Number((match[2] ?? "").slice(0, 2).padEnd(2, "0"));
  return Math.min(whole * SPLIT_SCALE + fraction, MAX_SPLIT_VALUE);
};

const splitValueFormat = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 2,
  useGrouping: false,
});

export const formatSplitValue = (value) =>
  splitValueFormat.format((value ?? 0) / SPLIT_SCALE);

const valueOf = (receiver) => receiver.value ?? 0;

export const sumValues = (receivers) =>
  receivers.reduce((sum, r) => sum + valueOf(r), 0);

export const getRemainingPercent = (receivers) =>
  FULL_PERCENT - sumValues(receivers);

export const isEqualSplit = (receivers) => {
  const shares = receivers.map((r) => r.share);
  return shares.length > 0 && Math.max(...shares) - Math.min(...shares) <= 1;
};

const withoutValues = (receivers) =>
  receivers.map(({ value: _value, ...receiver }) => receiver);

const applyValues = (totalCents, mode, receivers) => {
  const shares = splitSharesById(
    mode,
    totalCents,
    receivers.map((r) => r.id),
    receivers.map(valueOf),
  );
  return receivers.map((r, index) => ({
    id: r.id,
    value: valueOf(r),
    share: shares[index],
    locked: false,
  }));
};

const percentsFromShares = (receivers) => {
  const shares = receivers.map((r) => r.share);
  const values = shares.some((share) => share > 0)
    ? splitByWeights(FULL_PERCENT, shares)
    : splitAmount(FULL_PERCENT, receivers).map((r) => r.share);
  return receivers.map((r, index) => ({ ...r, value: values[index] }));
};

export const resplitForTotal = (totalCents, { mode, receivers }) => {
  if (isValueMode(mode)) {
    return { mode, receivers: applyValues(totalCents, mode, receivers) };
  }
  if (mode === SHARE_MODES.equal) {
    return { mode, receivers: equalSplit(totalCents, receivers) };
  }
  return { mode, receivers: resplitForAmount(totalCents, receivers) };
};

export const switchShareMode = (totalCents, split, mode) => {
  if (split.mode === mode) return split;
  const { receivers } = split;
  switch (mode) {
    case SHARE_MODES.equal:
      return { mode, receivers: equalSplit(totalCents, receivers) };
    case SHARE_MODES.amounts:
      return {
        mode,
        receivers: captureWeights(
          withoutValues(receivers).map((r) => ({ ...r, locked: false })),
        ),
      };
    case SHARE_MODES.weights:
      return {
        mode,
        receivers: applyValues(
          totalCents,
          mode,
          receivers.map((r) => ({ ...r, value: DEFAULT_WEIGHT })),
        ),
      };
    case SHARE_MODES.percent:
      return {
        mode,
        receivers: applyValues(totalCents, mode, percentsFromShares(receivers)),
      };
    default:
      return split;
  }
};

const valueForNewReceiver = (mode, receivers) =>
  mode === SHARE_MODES.percent
    ? Math.max(0, getRemainingPercent(receivers))
    : DEFAULT_WEIGHT;

export const toggleSplitReceiver = (totalCents, { mode, receivers }, id) => {
  const selected = receivers.some((r) => r.id === id);
  if (mode === SHARE_MODES.amounts) {
    return { mode, receivers: toggleReceiverShare(totalCents, receivers, id) };
  }
  const updated = selected
    ? receivers.filter((r) => r.id !== id)
    : [
        ...receivers,
        { id, share: 0, value: valueForNewReceiver(mode, receivers) },
      ];
  return mode === SHARE_MODES.equal
    ? { mode, receivers: equalSplit(totalCents, updated) }
    : { mode, receivers: applyValues(totalCents, mode, updated) };
};

export const selectSplitReceivers = (totalCents, { mode, receivers }, ids) => {
  if (ids.length === 0) return { mode, receivers: [] };
  const chosen = ids.map(
    (id) => receivers.find((r) => r.id === id) ?? { id, share: 0 },
  );
  if (mode === SHARE_MODES.weights) {
    return {
      mode,
      receivers: applyValues(
        totalCents,
        mode,
        chosen.map((r) => ({ ...r, value: r.value ?? DEFAULT_WEIGHT })),
      ),
    };
  }
  if (mode === SHARE_MODES.percent) {
    const percents = splitAmount(FULL_PERCENT, chosen);
    return {
      mode,
      receivers: applyValues(
        totalCents,
        mode,
        percents.map((r) => ({ ...r, value: r.share })),
      ),
    };
  }
  return { mode: SHARE_MODES.equal, receivers: equalSplit(totalCents, chosen) };
};

export const setSplitValue = (totalCents, { mode, receivers }, id, value) => {
  if (!isValueMode(mode) || !receivers.some((r) => r.id === id)) {
    return { mode, receivers };
  }
  const clamped = Math.min(Math.max(value, 0), MAX_SPLIT_VALUE);
  return {
    mode,
    receivers: applyValues(
      totalCents,
      mode,
      receivers.map((r) => (r.id === id ? { ...r, value: clamped } : r)),
    ),
  };
};

export const setSplitShare = (totalCents, split, id, cents) => {
  if (isValueMode(split.mode)) return split;
  const current =
    split.mode === SHARE_MODES.amounts
      ? split
      : switchShareMode(totalCents, split, SHARE_MODES.amounts);
  const receivers = setManualShare(totalCents, current.receivers, id, cents);
  return receivers === current.receivers
    ? split
    : { mode: SHARE_MODES.amounts, receivers };
};

export const SPLIT_MESSAGES = {
  noWeight: "Mindestens ein Anteil muss größer als 0 sein.",
};

const percentMessage = (receivers) => {
  const sum = formatSplitValue(sumValues(receivers));
  return `Die Prozente ergeben ${sum} % statt 100 %.`;
};

export const splitProblem = ({ mode, receivers }) => {
  if (mode === SHARE_MODES.percent && getRemainingPercent(receivers) !== 0) {
    return percentMessage(receivers);
  }
  if (mode === SHARE_MODES.weights && sumValues(receivers) === 0) {
    return SPLIT_MESSAGES.noWeight;
  }
  return null;
};

export const describePercentRemainder = (receivers) => {
  const remaining = getRemainingPercent(receivers);
  if (remaining === 0) return { balanced: true, text: "Verteilt: 100 %" };
  return {
    balanced: false,
    text:
      remaining > 0
        ? `Noch offen: ${formatSplitValue(remaining)} % von 100 %`
        : `${formatSplitValue(-remaining)} % zu viel verteilt`,
  };
};

export const removeLastDigit = (cents) => Math.floor(cents / 10);

export const getBalanceInCents = (participant) =>
  toCents(participant.gets) - toCents(participant.pays);
